<template>
  <Layout>
    <PageHeader :title="titleBody" :items="items" />
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <!-- Start Bar Buttons -->
          <div class="card-body">
            <div class="row">
              <div class="col-sm-12 col-md-6"></div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <button @click="refreshData" type="button" class="btn btn-primary">
                  <i class="bx bx-refresh"></i> Atualizar
                </button>
              </div>
            </div>
          </div>
          <!-- End Bar Buttons -->
          
          <div class="card-body">
            <!-- Loading -->
            <div v-if="loading" class="text-center">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Carregando...</span>
              </div>
              <p class="mt-2">Carregando permissões...</p>
            </div>

            <!-- Roles e Permissões -->
            <div v-else class="permissions-layout">
              <!-- Layout de 2 Colunas -->
              <div class="row no-gutters">
                <!-- Coluna Esquerda: Lista de Roles (25%) -->
                <div class="col-md-3 roles-sidebar">
                  <div class="roles-list-container">
                    <div class="roles-header">
                      <h5 class="mb-0">Perfis</h5>
                      <span class="roles-count">({{ roles.length }})</span>
                    </div>
                    <div class="roles-list">
                      <div 
                        v-for="role in roles" 
                        :key="role.id"
                        class="role-item"
                        :class="{ 'active': Number(role.id) === Number(selectedRoleId) }"
                        @click="selectRole(role.id)"
                      >
                        <div class="role-item-content">
                          <div class="role-description">{{ role.id }} {{ role.description || 'Sem descrição' }}</div>
                          <div class="role-name">{{ role.name }}</div>
                        </div>
                      </div>
                    </div>
                    <!-- Caso não haja roles -->
                    <div v-if="roles.length === 0" class="text-center p-3">
                      <div class="alert alert-warning mb-0">
                        <i class="bx bx-info-circle"></i>
                        Nenhum perfil encontrado.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Coluna Direita: Permissões (75%) -->
                <div class="col-md-9 permissions-content">
                  <div class="permissions-container">
                    <div v-if="selectedRoleId && selectedRole">
                      <div class="permissions-header mb-3">
                        <h5 class="mb-2">
                          Permissões do perfil: <strong>{{ selectedRole.description }}</strong>
                        </h5>
                        <p class="text-muted mb-3">
                          Gerencie as permissões do perfil. Marque ou desmarque as permissões desejadas.
                        </p>
                        <div class="permissions-actions">
                          <button 
                            @click="selectAllPermissions(selectedRoleId)" 
                            class="btn btn-sm btn-outline-success mr-2"
                          >
                            Marcar Todas
                          </button>
                          <button 
                            @click="deselectAllPermissions(selectedRoleId)" 
                            class="btn btn-sm btn-outline-secondary mr-2"
                          >
                            Desmarcar Todas
                          </button>
                          <button 
                            @click="saveRolePermissions(selectedRole)" 
                            class="btn btn-sm btn-success"
                            :disabled="savingRole === selectedRoleId"
                          >
                            <span v-if="savingRole === selectedRoleId">
                              <i class="bx bx-loader-alt bx-spin"></i> Salvando...
                            </span>
                            <span v-else>
                              <i class="bx bx-save"></i> Salvar
                            </span>
                          </button>
                        </div>
                      </div>
                      
                      <!-- TreeView de Permissões -->
                      <div class="permissions-tree-container">
                        <div v-if="permissionsTree.length > 0" class="permissions-tree">
                          <PermissionTreeNode
                            v-for="node in permissionsTree"
                            :key="`${node.name}-${selectedRoleId}-${selectedPermissionsSignature}`"
                            :node="node"
                            :level="0"
                            :roleId="selectedRoleId"
                            :selectedPermissions="selectedPermissionsArray"
                            @permission-change="handlePermissionChange"
                            @permissions-batch-change="handleMultiplePermissionChanges"
                          />
                        </div>
                        <div v-else class="text-center p-4">
                          <div class="alert alert-info mb-0">
                            <i class="bx bx-info-circle"></i>
                            Nenhuma permissão disponível para exibir.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-5">
                      <div class="alert alert-info">
                        <i class="bx bx-info-circle"></i>
                        Selecione um perfil na lista ao lado para gerenciar suas permissões.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import appConfig from "@/app.config";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import PermissionTreeNode from "@/components/PermissionTreeNode.vue";
import { permissionService } from "@/helpers/easyindustriaapi/permission.service";

export default {
  page: {
    title: 'Permissões',
    meta: [ {name: 'description', content: appConfig.description }],
  },
  components: { Layout, PageHeader, PermissionTreeNode },
  data() {
    return {
      titleBody: 'Gerenciar Permissões',
      items: [
        {
          text: "Dashboard",
          href: "/",
        },
        {
          text: "Usuários",
          href: "/",
        },
        {
          text: "Permissões",
          href: "/usuarios/permissoes",
          active: true,
        }
      ],
      roles: [],
      allPermissions: [],
      rolePermissions: {},
      loading: true,
      savingRole: null,
      selectedRoleId: null,
    }
  },
  computed: {
    selectedRole() {
      if (!this.selectedRoleId) return null;
      return this.roles.find(role => Number(role.id) === Number(this.selectedRoleId)) || null;
    },
    permissionsTree() {
      if (!this.allPermissions || this.allPermissions.length === 0) {
        return [];
      }
      
      // Agrupar permissões por prefixo (ex: "agenda.view" -> grupo "agenda")
      const groups = {};
      
      this.allPermissions.forEach(permission => {
        const parts = permission.name.split('.');
        const groupName = parts[0]; // Primeira parte é o grupo
        
        if (!groups[groupName]) {
          groups[groupName] = {
            name: groupName,
            description: this.capitalizeFirst(groupName),
            children: []
          };
        }
        
        // Adicionar permissão como filho do grupo
        groups[groupName].children.push({
          id: permission.id,
          name: permission.name,
          description: permission.description || permission.name
        });
      });
      
      // Converter objeto em array e ordenar
      return Object.values(groups).sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    },
    selectedPermissionsArray() {
      if (this.selectedRoleId == null) return [];
      const key = Number(this.selectedRoleId);
      const ids = this.rolePermissions[key] || this.rolePermissions[this.selectedRoleId] || [];
      return Array.isArray(ids)
        ? ids.map(id => Number(id)).filter(id => !Number.isNaN(id))
        : [];
    },
    /** Assinatura da seleção atual para usar em :key e forçar re-render dos checkboxes quando a seleção mudar */
    selectedPermissionsSignature() {
      const arr = this.selectedPermissionsArray;
      return arr.length ? arr.slice().sort((a, b) => a - b).join(',') : '';
    }
  },
  created() {
    try {
      this.currentUser = JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      this.currentUser = null;
    }
    try {
      this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa') || 'null');
    } catch {
      this.currentEmpresa = null;
    }
  },
  mounted() {
    this.refreshData();
  },
  methods: {
    makeToast(variant = null, message = 'Error generic') {
      this.$bvToast.toast(message, {
        title: `Notificação`,
        toaster: 'b-toaster-bottom-right',
        variant: variant,
        solid: true,
        appendToast: true
      });
    },
    onLoader() {
      if (document.getElementById("preloader").style.display === "none") {
        document.getElementById("preloader").style.display = "block";
        document.getElementById("status").style.display = "block";
      }
    },
    offLoader() {
      if (document.getElementById("preloader").style.display === "block") {
        setTimeout(function () {
          document.getElementById("preloader").style.display = "none";
          document.getElementById("status").style.display = "none";
        }, 2500);
      }
    },
    async getData() {
      this.onLoader();
      try {
        if (!this.currentEmpresa || this.currentEmpresa.id == null) {
          this.makeToast('danger', 'Selecione uma empresa antes de gerenciar permissões.');
          this.offLoader();
          return;
        }

        const response = await permissionService.getAll(this.currentEmpresa.id);

        if (response.success) {
          const data = response.data || response;
          const rolesRaw = (data.roles != null ? data.roles : (Array.isArray(data) ? data : []));
          this.roles = Array.isArray(rolesRaw) ? rolesRaw : [];

          // Usar a lista completa de permissões retornada pela API (todas da tabela permissions),
          // para exibir todas e marcar apenas as que têm relacionamento com o perfil selecionado.
          let rawAll =
            data.all_permissions != null
              ? data.all_permissions
              : data.allPermissions != null
                ? data.allPermissions
                : [];

          // Resposta legada ou API sem all_permissions: preencher via GET /api/permissions
          if (!Array.isArray(rawAll) || rawAll.length === 0) {
            try {
              const listRes = await permissionService.listAll();
              const inner = listRes && listRes.data ? listRes.data : listRes;
              if (inner && Array.isArray(inner.permissions)) {
                rawAll = inner.permissions;
              }
            } catch {
              /* mantém rawAll vazio; mensagem abaixo se nada a exibir */
            }
          }

          this.allPermissions = (Array.isArray(rawAll) ? rawAll : [])
            .map(p => (p != null && (p.id !== undefined && p.id !== null)) ? { ...p, id: Number(p.id) } : null)
            .filter(Boolean)
            .filter(p => !String(p.name || '').includes('empresa.') &&
                        !String(p.name || '').includes('cidade.') &&
                        !String(p.name || '').includes('estado.') &&
                        !String(p.name || '').includes('pais.'));

          // Limpar e repopular com $set por chave para garantir reatividade do Vue
          this.rolePermissions = {};
          this.roles.forEach(role => {
            const roleKey = Number(role.id);
            const perms = role.permissions;
            const ids = Array.isArray(perms)
              ? perms
                  .map(p => (p != null && (p.id !== undefined && p.id !== null)) ? Number(p.id) : null)
                  .filter(id => id != null)
              : [];
            this.$set(this.rolePermissions, roleKey, ids);
            if (String(roleKey) !== String(role.id)) {
              this.$set(this.rolePermissions, role.id, ids);
            }
          });

          if (this.roles.length > 0) {
            const currentId = this.selectedRoleId != null ? Number(this.selectedRoleId) : null;
            const exists = currentId != null && this.roles.some(r => Number(r.id) === currentId);
            if (!exists) {
              this.selectedRoleId = Number(this.roles[0].id);
            }
          } else {
            this.selectedRoleId = null;
          }

          this.offLoader();
        } else {
          console.error('API retornou success: false');
          this.makeToast('danger', 'Erro: API retornou success false');
          this.offLoader();
        }
      } catch (error) {
        let messageErro = error.response ? error.response.data : error;

        if (error.response) {
          switch (error.response.status) {
            case 406:
              this.makeToast('danger', 'Erro 406: ' + (messageErro.message || 'Erro de validação'));
              this.offLoader();
              break;
            case 404:
              this.makeToast('danger', 'Erro 404: endpoint não encontrado ou servidor fora do ar');
              this.offLoader();
              break;
            default:
              this.makeToast('danger', messageErro.message || error.message);
              this.offLoader();
              break;
          }
        } else {
          this.makeToast('danger', error.message || 'Erro ao carregar permissões');
          this.offLoader();
        }
      }
    },
    async refreshData() {
      this.loading = true;
      await this.getData();
      this.loading = false;
    },
    selectAllPermissions(roleId) {
      const key = Number(roleId);
      const allPermissionIds = this.allPermissions.map(p => Number(p.id));
      this.$set(this.rolePermissions, key, [...allPermissionIds]);
      if (key !== roleId) this.$set(this.rolePermissions, roleId, [...allPermissionIds]);
    },
    deselectAllPermissions(roleId) {
      const key = Number(roleId);
      this.$set(this.rolePermissions, key, []);
      if (key !== roleId) this.$set(this.rolePermissions, roleId, []);
    },
    async saveRolePermissions(role) {
      try {
        this.savingRole = Number(role.id);
        const key = Number(role.id);
        const permissions = this.rolePermissions[key] || this.rolePermissions[role.id] || [];

        const response = await permissionService.updateRolePermissions(
          role.id,
          permissions,
          this.currentEmpresa.id
        );

        if (response.success) {
          this.makeToast('success', `Permissões do perfil "${role.description}" atualizadas com sucesso!`);
          
          // Recarregar dados da API para garantir sincronização
          await this.refreshData();
        } else {
          this.makeToast('danger', response.message || 'Erro ao salvar permissões');
        }
      } catch (error) {
        // Tratar erros de rede
        if (error.isNetworkError || error.message === 'Failed to fetch' || error.name === 'TypeError') {
          this.makeToast('danger', 'Erro de conexão. Verifique sua internet e se o servidor está acessível.');
          this.savingRole = null;
          return;
        }
        
        let messageErro = error.response ? error.response.data : error;
        
        // Tratar diferentes tipos de erro HTTP
        if (error.response) {
          if (error.response.status === 400) {
            this.makeToast('danger', messageErro.message || 'Dados inválidos. Verifique as permissões selecionadas.');
          } else if (error.response.status === 404) {
            this.makeToast('danger', 'Perfil não encontrado.');
          } else if (error.response.status === 500) {
            this.makeToast('danger', 'Erro no servidor. Tente novamente mais tarde.');
          } else {
            this.makeToast('danger', messageErro.message || error.message || 'Erro ao salvar permissões');
          }
        } else {
          // Erro sem resposta (pode ser erro de parse ou outro)
          this.makeToast('danger', error.message || 'Erro ao salvar permissões. Tente novamente.');
        }
      } finally {
        this.savingRole = null;
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return "N/A";
      }
    },
    selectRole(roleId) {
      this.selectedRoleId = roleId != null ? Number(roleId) : null;
    },
    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    handlePermissionChange(permissionId, checked) {
      if (this.selectedRoleId == null) return;
      const key = Number(this.selectedRoleId);
      const current = this.rolePermissions[key] || this.rolePermissions[this.selectedRoleId] || [];
      const currentPermissions = current.map(id => Number(id));
      const idNum = Number(permissionId);

      if (checked) {
        if (!currentPermissions.includes(idNum)) currentPermissions.push(idNum);
      } else {
        const idx = currentPermissions.indexOf(idNum);
        if (idx > -1) currentPermissions.splice(idx, 1);
      }

      this.$set(this.rolePermissions, key, currentPermissions);
      if (key !== this.selectedRoleId) this.$set(this.rolePermissions, this.selectedRoleId, currentPermissions);
    },
    handleMultiplePermissionChanges(permissionIds, checked) {
      if (this.selectedRoleId == null || !Array.isArray(permissionIds) || permissionIds.length === 0) return;

      const key = Number(this.selectedRoleId);
      const current = this.rolePermissions[key] || this.rolePermissions[this.selectedRoleId] || [];
      const currentPermissions = current.map(id => Number(id));
      const idsNum = permissionIds.map(id => Number(id));

      const updatedPermissions = checked
        ? [...new Set([...currentPermissions, ...idsNum])]
        : currentPermissions.filter(id => !idsNum.includes(id));

      this.$set(this.rolePermissions, key, updatedPermissions);
      if (key !== this.selectedRoleId) this.$set(this.rolePermissions, this.selectedRoleId, updatedPermissions);
    },
  },
};
</script>

<style scoped>
.permissions-layout {
  min-height: 500px;
}

/* Coluna Esquerda - Lista de Roles */
.roles-sidebar {
  border-right: 1px solid #dee2e6;
  background-color: #f8f9fa;
  min-height: 500px;
}

.roles-list-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.roles-header {
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.roles-header h5 {
  color: #dc3545;
  font-weight: 600;
  margin: 0;
}

.roles-count {
  color: #6c757d;
  font-size: 0.9em;
}

.roles-list {
  flex: 1;
  overflow-y: auto;
  background-color: #fff;
}

.role-item {
  padding: 12px 20px;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: #fff;
}

.role-item:hover {
  background-color: #f8f9fa;
}

.role-item.active {
  background-color: #e9ecef;
  border-left: 3px solid #dc3545;
}

.role-item-content {
  display: flex;
  flex-direction: column;
}

.role-description {
  font-weight: 500;
  color: #212529;
  margin-bottom: 4px;
  font-size: 0.95em;
}

.role-name {
  font-size: 0.85em;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Coluna Direita - Permissões */
.permissions-content {
  background-color: #fff;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.permissions-container {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permissions-header {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 15px;
  flex-shrink: 0;
}

.permissions-header h5 {
  color: #212529;
  font-weight: 600;
}

.permissions-actions {
  margin-top: 15px;
}

/* TreeView Container */
.permissions-tree-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 20px;
  padding-right: 10px;
  max-height: calc(100vh - 400px);
  min-height: 300px;
}

.permissions-tree {
  position: relative;
}

/* Estilização para TreeView */
.permissions-tree-container >>> .permission-tree-node {
  margin-bottom: 2px;
}

.permissions-tree-container >>> .tree-checkbox >>> .custom-control-input:indeterminate ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.permissions-tree-container >>> .tree-checkbox >>> .custom-control-input:indeterminate ~ .custom-control-label::after {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-width='2' d='M3 6h6'/%3e%3c/svg%3e");
}

.permission-item {
  padding: 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.permission-item:hover {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.custom-control-label {
  cursor: pointer;
}

.custom-control-label:hover {
  background-color: transparent;
}

.accordion .card {
  border: 1px solid #dee2e6;
}

.accordion .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.btn-link {
  text-decoration: none;
  color: #495057;
}

.btn-link:hover {
  text-decoration: none;
  color: #007bff;
}

.badge {
  font-size: 0.875em;
}

.alert {
  border-radius: 8px;
}

/* Responsividade */
@media (max-width: 768px) {
  .roles-sidebar {
    border-right: none;
    border-bottom: 1px solid #dee2e6;
    margin-bottom: 20px;
  }
  
  .permissions-content {
    padding-left: 15px;
    padding-right: 15px;
  }
}
</style>